/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useCallback, useEffect, useRef } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { omit } from "lodash"

/**
 * Every drawer that opts into back-dismissal, enumerated here rather than as a
 * string per call site so the one-slot rule below stays reviewable in a single
 * place instead of by grep.
 */
export type BackDismissibleDrawerName =
  | "productPhotoUpload"
  | "variationPhotoUpload"
  | "categoryPicker"
  | "removeItem"
  | "videoPreview"
  | "pmfFeedback"
  | "pmfFeedbackNotification"
  | "productLocationEdit"

interface UseBackDismissibleDrawerParams {
  /** Identifies this drawer's history entry. */
  name: BackDismissibleDrawerName
}

interface UseBackDismissibleDrawerResult {
  open: boolean
  /**
   * Boolean-only, unlike a `useState` setter: there is no updater form, because
   * the previous value lives on the history entry rather than in React state.
   * It still drops straight into drawer props typed `(val: boolean) => void`
   * (`onTogglePhotoUploaderVisible`).
   */
  setOpen: (next: boolean) => void
}

/** The open-drawer slice of `location.state`; absent means none is open. */
interface DrawerLocationState {
  drawer?: BackDismissibleDrawerName
}

/**
 * Visibility for a drawer that the browser's back button should *close* rather
 * than navigate away from.
 *
 * Same shape as {@link useWizard}: the state lives on the history entry and
 * opening is a `push`, so the drawer gets an entry of its own. A POP — the
 * browser back button, and the app webview's hardware back — restores the entry
 * underneath, which carries no `drawer`, so the drawer closes and the merchant
 * stays on the page. That matters most in the webview, where the drawer is
 * full-screen (`width={md ? 480 : "100%"}`) and looks like a screen the back
 * button ought to dismiss, but nothing here is width- or webview-gated: the
 * entry is pushed the same way at every size.
 *
 * The pathname never changes, so pushing does not remount the page or reset the
 * form — the state the caller keeps alongside `open` (which image is being
 * edited, the pre-crop file cache) survives both the push and the pop.
 *
 * NEVER RESTORED, ONLY OPENED — unlike {@link useWizard}, which treats the entry
 * as authoritative however it was reached, this hook opens the drawer only when
 * *this* mount asks it to and it has stayed open since. Any other flag it finds —
 * from a reload, or from a forward press onto an entry the drawer was closed on —
 * is dropped on sight. A wizard step is self-describing; an open drawer is not.
 * Its meaning lives in the caller's component state, which the caller resets on
 * close and the merchant mutates afterwards (deleting or reordering images
 * renumbers the very index the drawer was opened with), so a restored drawer
 * writes to the wrong thing — see the effect below for what that costs. The trade
 * is that back is the only way out and forward never brings the drawer back;
 * reopening is a click on the same tile, and nothing in the ticket asks for more.
 *
 * ONE AT A TIME — `location.state` holds a single `drawer` slot, so this is not
 * "unique names make several drawers work": opening a second back-dismissible
 * drawer overwrites the first, which then reads as closed. Names keep unrelated
 * drawers from popping each other's entry; they do not make stacking work. Every
 * caller owns one visible overlay at a time, so the slot is enough.
 *
 * That exclusivity holds in the STEADY STATE only, and it is not what the stack
 * relies on: an overlay that opens asynchronously leaves a window with no mask
 * up at all (`BasicInformationForm.handleOpenUploadDrawer` awaits a FileReader
 * before opening), and a second overlay can be tapped inside it. So `setOpen`
 * takes a claimed slot over with `replace` rather than `push` — see there. The
 * pairs below are why only one overlay is ever *visible*; they are not a promise
 * that only one can ever be *asked for*.
 *
 * The product add/edit callers are mask-exclusive in the steady state:
 *
 *   - `productPhotoUpload`, `categoryPicker` and `videoPreview` all open from the
 *     step-1 panel, and each covers it — the photo drawer is full-screen below
 *     `md` and masked above, the category picker is a `height="100%"` bottom
 *     Drawer below `md` and a masked Modal above, the video preview is a centred
 *     Modal. Whichever is up, the triggers for the other two are behind its mask.
 *   - `variationPhotoUpload` lives in a different step panel. The wizard renders
 *     every panel, but the inactive ones are `display: none`, so its trigger is
 *     unclickable from step 1 and vice versa — which settles it against the
 *     step-1 three, and *only* against those three.
 *   - `removeItem` is the only one outside the step panels (the footer's ⊖), so
 *     it is the one that shares a screen with others. Two pairs, not one:
 *     with the step-1 three when a mobile edit is deep-linked to step 1, and
 *     with the two variations-step drawers — `variationPhotoUpload` and
 *     `productLocationEdit` — when it is deep-linked to the variations step
 *     — `SECTION_STEP_KEYS` maps `VARIATION_LIST` to `"variations"`
 *     (`ProductDetails.tsx:174`) and the sticky "Edit stock" toast links there,
 *     so `showSaveActions` puts `[⊖][Save]` under a live `VariationTable`.
 *     Both pairs are still exclusive: the ⊖ sits behind whichever mask is up,
 *     the remove sheet's own mask covers the panel while it is up, and
 *     `VariationTable.handleOpenUploadDrawer` (`:223`) is synchronous, so it
 *     opens no mask-less window of the kind described above.
 *   - `productLocationEdit` opens from the location cells of `VariationTable`
 *     (the variations step) and of `ProductsTable` on the products list page,
 *     where it is the only back-dismissible drawer. In the wizard it therefore
 *     shares the variations panel with `variationPhotoUpload` and `removeItem`,
 *     and the same reasoning settles both pairs: it is full-screen below `md`
 *     and masked above, so the other two's triggers sit behind its mask, and both
 *     of its entry points open synchronously
 *     (`VariationTable.handleOpenLocationDrawer`, `:245`;
 *     `ProductsTable.renderStockCell`, `:1120`).
 *
 * PMF feedback uses two names to replace its form sheet with its notification
 * sheet in place after submit. They are a transition, not stacked overlays.
 *
 * A caller that can genuinely stack — one that stays open and interactive
 * *over* another — does not get a name; it needs the slot turned into a stack
 * first. Merely being reachable in a race is handled by the takeover above.
 *
 * It is also the same slot {@link useWizard} stores `step`/`wizardPushed` in,
 * and the wizard spreads whatever state it finds when it pushes a step — so
 * navigating the wizard while a drawer is open would carry the flag onto the new
 * step and leave the drawer up. No caller can do that today (the drawer is
 * full-screen below `md` and masked above it, so the step controls are
 * unreachable while it is open), but a caller that can navigate behind an open
 * drawer has to close it first.
 */
export const useBackDismissibleDrawer = ({
  name,
}: UseBackDismissibleDrawerParams): UseBackDismissibleDrawerResult => {
  const history = useHistory<DrawerLocationState>()
  const location = useLocation<DrawerLocationState>()

  // Whether the ENTRY says this drawer is open. Read from the subscribed
  // `location` rather than `history.location`, since this is what has to
  // re-render the caller when an entry is pushed or popped. On its own it is a
  // claim, not permission — the latch below decides whether this mount honours
  // it.
  const hasDrawerEntry = location.state?.drawer === name

  // Whether this mount pushed the entry AND the drawer has been open ever since,
  // which is what makes the flag trustworthy: the caller's context (which image
  // is being edited, which variation row) is component state, so only a mount
  // that has held the drawer open continuously still holds a context that
  // matches. Set on push rather than derived, because the entry itself cannot
  // say which mount wrote it — and spent the moment the drawer closes, by ANY
  // route, because from that moment the caller is free to move on:
  //
  //   - it resets on close (`BasicInformationForm`'s submit clears
  //     `editingImageIndex`), so a restored drawer converts an edit into an
  //     uncapped append; or
  //   - the merchant is back on the form and reorders or deletes images, which
  //     rewrite the list positionally (`handleRemoveImage`, `handleDragEnd`) and
  //     leave `editingImageIndex` pointing at the old position — so a restored
  //     drawer's submit writes over the wrong image, or spreads `undefined` into
  //     an entry with no `id`.
  //
  // Three review rounds each carved out one "safe" way for the flag to outlive
  // the drawer (the entry is authoritative; any close within the mount; a pop
  // specifically), and each was reachable. The hook cannot see the caller's
  // state, so it cannot tell a harmless interval from a mutating one — hence no
  // carve-out at all. A closed drawer is never restorable from history.
  const openedHereRef = useRef(false)

  // Set between asking for a pop and the pop landing. `goBack` is asynchronous
  // against a real browser history — the pop arrives on `popstate`, a task later
  // — so until then `history.location` still names this drawer and a second
  // close would pass the state guard below and pop again, ejecting the merchant
  // from a half-filled form. That is reachable: rc-drawer routes the close icon,
  // a mask click (`maskClosable` defaults true) and *every* ESC keydown
  // (`keyboard` defaults true, `DrawerPopup` fires `onClose` per keydown with no
  // guard of its own) into the same handler, and `destroyOnClose` only unmounts
  // after the close animation, so the panel stays live and focused throughout
  // the window.
  const popPendingRef = useRef(false)

  // Both conditions, at render time rather than repaired afterwards. Deriving
  // visibility from the entry alone would show the drawer for the paint before
  // the cleanup effect below lands: harmless in itself, but it mounts
  // `PhotoUploadDrawer` with a context that does not match, whose own effect
  // (`PhotoUploadDrawer.tsx:264`) then fetches and base64-converts the stale
  // image before the panel is torn down again. Gating here means a restored
  // entry never renders as open at all.
  //
  // Reading a ref during render is safe in the only direction that matters: it
  // is set synchronously in `setOpen` immediately before the push that schedules
  // the re-render, so no render that follows an open can miss it, and every
  // other read wants the `false`.
  const open = hasDrawerEntry && openedHereRef.current

  // Any location change means the pop we asked for (or something else) landed,
  // so the pop latch is spent. Keyed on the location object rather than `key`,
  // which is undefined on the entry a fresh browser history starts on.
  //
  // The same change is where the mount latch is spent, and it is deliberately
  // spent on the drawer's entry going away rather than on how it went: a browser
  // pop does not run `setOpen`, and it is the close that hands the merchant back
  // a form they can reorder or delete images on.
  useEffect(() => {
    popPendingRef.current = false
    if (!hasDrawerEntry) openedHereRef.current = false
  }, [location, hasDrawerEntry])

  // Drop any flag the latch does not vouch for. `location.state` survives a
  // reload (`history.js` seeds the initial location from `window.history.state`)
  // and a forward press onto an entry the drawer was closed on, but the caller's
  // context does not — so honouring the flag would reopen a drawer that cannot do
  // its job. In `BasicInformationForm` that is one of two corruptions: with
  // `editingImageIndex` reset, `handleSubmitUploadPhoto` takes the append branch
  // and walks past `MAX_PRODUCT_IMAGES` (only the add tile and the file picker
  // enforce it); with it stale after a delete or a reorder, the edit lands on the
  // wrong image, or on `newImages[i] === undefined` — spreading into an entry
  // with no `id`, which is what keys the tiles and gets saved.
  //
  // The drawer is already not showing by the time this runs (`open` requires the
  // latch); what this clears is the claim left on the entry, which would
  // otherwise make the next `setOpen(true)` look like a no-op to the
  // already-in-requested-state guard below and leave the drawer unopenable.
  //
  // Replace rather than pop: this entry is where the merchant is, and after a
  // reload there may be nothing behind it — popping would leave the page, which
  // is the very thing this hook exists to prevent.
  useEffect(() => {
    if (!hasDrawerEntry || openedHereRef.current) return

    const current = history.location
    history.replace({ ...current, state: omit(current.state, ["drawer"]) })
  }, [hasDrawerEntry, history])

  const setOpen = useCallback(
    (next: boolean) => {
      // Read the LIVE entry rather than the render's captured `location`, as
      // `ProductDetails` does around its own step jump. Two reasons: a caller
      // holding this callback from an earlier render would otherwise act on a
      // stale snapshot, and reading it here is what keeps the callback stable
      // across renders — `VariationTable` feeds it into the memo that builds the
      // table columns, which would otherwise recompute on every render.
      const current = history.location

      // Already in the requested state — opening twice would bank an entry whose
      // back press appears dead.
      if ((current.state?.drawer === name) === next) return

      if (!next) {
        // The state guard above cannot catch a second close while a pop is still
        // in flight, because the entry it reads has not changed yet.
        if (popPendingRef.current) return
        popPendingRef.current = true

        // The mount latch is not spent here but in the location effect above,
        // which catches this close and a browser pop alike — this branch only
        // runs for the former.
        //
        // Pop rather than clear the flag in place: a `replace` would leave the
        // stack one entry deeper than the merchant walked, so their next back
        // press would land on the pre-open entry and look inert.
        history.goBack()
        return
      }

      // The slot is already claimed by a different drawer. Take it over in
      // place: pushing would bank a second entry for what is only ever one
      // visible overlay, and the merchant would need two back presses to leave
      // — the first closing this drawer, the second landing on the previous
      // drawer's stripped entry and looking completely inert.
      //
      // Reachable despite every caller pair being mask-exclusive in the steady
      // state, because a caller may open asynchronously and leave a window with
      // no mask up at all: `BasicInformationForm.handleOpenUploadDrawer` awaits
      // `readFileAsDataURL` before opening when re-editing a photo whose
      // pre-crop `File` is cached, and the category field sits on the same
      // panel. So the one-slot rule is enforced here rather than left to an
      // invariant every future caller would have to re-establish.
      //
      // Overwriting is what the slot already does — the losing drawer reads as
      // closed either way, and its instance spends its latch on the location
      // change. This only stops the stack growing by an entry nothing owns.
      const takeoverInPlace = Boolean(current.state?.drawer)

      // Spread the whole location: history v4 resolves only `pathname` against
      // the current entry, so an object push that omits `search` drops it —
      // taking the `?webview=true` override with it. The wizard's `step` and
      // anything else riding on `state` has to survive the push for the same
      // reason: the entry underneath is the step the drawer was opened from, and
      // the drawer entry has to name that same step.
      const target = {
        ...current,
        state: { ...(current.state ?? {}), drawer: name },
      }

      openedHereRef.current = true
      if (takeoverInPlace) history.replace(target)
      else history.push(target)
    },
    [history, name],
  )

  return { open, setOpen }
}
